import * as THREE from "three";
import React, { useState, useEffect, useRef, Suspense } from "react";
import PropTypes from "prop-types";
import { Canvas, extend, useThree, useFrame } from "@react-three/fiber";
import flatten from "lodash-es/flatten";
import { SVGLoader as loader } from "three/examples/jsm/loaders/SVGLoader";
import LOGO_URL from "./logo.svg";
import { SceneController } from "../../../kandinsky/components/SceneController";

import { RectAreaLightHelper } from "three/examples/jsm/helpers/RectAreaLightHelper.js";
import { RectAreaLightUniformsLib } from "three/examples/jsm/lights/RectAreaLightUniformsLib.js";

import { Center, useTexture } from "@react-three/drei";
import { useWindowSize } from "react-use";

import "./styles.scss";

import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

RectAreaLightUniformsLib.init();

const texloader = new THREE.CubeTextureLoader();
texloader.setPath("/assets/img/cubemap@1024/");

const textureCube = texloader.load([
    "px.jpg",
    "nx.jpg",
    "py.jpg",
    "ny.jpg",
    "pz.jpg",
    "nz.jpg",
]);

extend({ OrbitControls });

// Promise of an SVG parsed into paths
// with which the threejs engine will make shapes
const svgResource = new Promise((resolve) =>
    new loader().load(LOGO_URL, (shapes) => {
        resolve(
            flatten(
                shapes.paths.map((group, index) => {
                    const shapes = loader.createShapes(group);
                    return shapes.map((shape) => {
                        const fillColor = group.userData.style.fill;
                        return { shape, color: fillColor, index };
                    });
                })
            )
        );
    })
);

/**
 * A very special thanks to @neftaly for open source contribution.
 * https://gist.github.com/neftaly/7c4d96f1ba37aada7f366b5393e59ddb
 *
 * Use a shape of the SVG to associate a Mesh Material with a Geometry
 */
function SvgShape({ shape, color, index }) {
    const mesh = useRef();
    return (
        <mesh ref={mesh}>
            <shapeBufferGeometry attach="geometry" args={[shape]} />
            <meshBasicMaterial
                aspect={window.innerWidth / window.innerHeight}
                attach="material"
                color={color}
                opacity={1}
                side={THREE.DoubleSide}
                flatShading={true}
                depthWrite={true}
                /*
          HACK: Offset SVG polygons by index
          The paths from SVGLoader Z-fight.
          This fix causes stacking problems with detailed SVGs.
        */
                polygonOffset
                polygonOffsetFactor={index * -0.1}
            />
        </mesh>
    );
}

function ExtrudedSvgShape({ shape, color, index }) {
    const mesh = useRef();
    const { gl } = useThree();

    const txUrls = {
        //displacementMap: "/assets/img/textures/metal-clean/displacement.jpeg",
        roughnessMap: "/assets/img/textures/metal-clean/roughness.jpeg",
        normalMap: "/assets/img/textures/metal-clean/normal.jpeg",
    };

    const tex = useTexture(txUrls);

    Object.keys(txUrls).forEach((k) => {
        tex[k].wrapS = THREE.RepeatWrapping;
        tex[k].wrapT = THREE.RepeatWrapping;
        tex[k].repeat.set(0.1, 0.1);
    });

    const extrusionSettings = {
        steps: 50,
        depth: 10,
        bevelEnabled: true,
        bevelThickness: 1,
        bevelSize: 1,
        bevelOffset: 0,
        bevelSegments: 10,
    };

    return (
        <mesh ref={mesh} position={[0, 0, 0]}>
            <extrudeBufferGeometry
                args={[shape, extrusionSettings]}
                receiveShadow
                castShadow
            />
            <meshPhysicalMaterial
                {...tex}
                roughnessScale={new THREE.Vector2(0.05, 0.05)}
                normalScale={new THREE.Vector2(0.05, 0.05)}
                rotation={Math.PI / 2}
                aspect={window.innerWidth / window.innerHeight}
                attach="material"
                color={0xffffff}
                opacity={1}
                side={THREE.DoubleSide}
                flatShading={true}
                wireframe={false}
                depthWrite={true}
                envMap={textureCube}
                roughness={0}
                reflectivity={1}
                metalness={0.98}
                clearcoat={1}
                anisotropy={gl.capabilities.getMaxAnisotropy()}
                /*
          HACK: Offset SVG polygons by index
          The paths from SVGLoader Z-fight.
          This fix causes stacking problems with detailed SVGs.
        */
                polygonOffset
                polygonOffsetFactor={index * -0.1}
            />
        </mesh>
    );
}

function Scene() {
    const [shapes, set] = useState([]);
    useEffect(() => svgResource.then(set), []);

    if (!shapes.length) return null;

    return (
        <SceneController
            frame={({ refs }) => {
                //refs["group"].rotation.y -= 0.01;
            }}
            mount={({ refs }) => {
                if (refs["svg"]) {
                    const ref = refs["svg"];

                    const bounds = new THREE.Box3();
                    bounds.setFromObject(ref);

                    if (Math.abs(bounds.max.x) !== Infinity) {
                        let width = Math.abs(bounds.max.x - bounds.min.x);
                        let height = Math.abs(bounds.min.y - bounds.max.y);
                        let depth = Math.abs(bounds.min.z - bounds.max.z);

                        if (!isNaN(width)) {
                            let x = -width / 2;
                            let y = -height / 2;
                            let z = -depth / 2;

                            ref.position.set(x, y, z);
                        }
                    }

                    //ref.position.x = width / 2;
                    //ref.position.y = bounds.min.y / 2;
                    //ref.position.z = bounds.max.z / 2;
                }
                //console.log('ref',ref);
            }}
        >
            {({ register }) => (
                <group
                    color={new THREE.Color(0x000000)}
                    position={[0, 0, 0]}
                    scale={[1, 1, 1]}
                    ref={(ref) => {
                        register("group", ref);
                    }}
                    rotation={[
                        THREE.Math.degToRad(0),
                        THREE.Math.degToRad(180),
                        THREE.Math.degToRad(180),
                    ]}
                >
                    <group
                        ref={(ref) => {
                            register("svg", ref);
                        }}
                    >
                        {shapes.map((item) => (
                            <ExtrudedSvgShape key={item.shape.uuid} {...item} />
                        ))}
                    </group>
                </group>
            )}
        </SceneController>
    );
}

const Camera = () => {
    const {
        camera,
        gl: { domElement },
    } = useThree();

    const controls = useRef();
    const { width, height } = useWindowSize();
    const isLandscape = width >= height;
    const curOrientation = isLandscape ? "landscape" : "portrait";

    const orientationWas = useRef(false);
    const orientation = useRef(curOrientation);

    camera.position.z = isLandscape ? 120 : 300;

    useFrame(() => {
        if (orientationWas.current != curOrientation) {
            orientationWas.current = orientation.current;
            orientation.current = curOrientation;
            controls.current.enabled = false;
            camera.position.z = isLandscape ? 120 : 300;
            controls.current.reset();
            controls.current.enabled = true;
        } else {
            controls.current.update();
        }
    });

    return (
        <orbitControls
            ref={controls}
            args={[camera, domElement]}
            autoRotate={true}
            enableZoom={true}
        />
    );
};

/**
 * Put it all together.  Flat Shviti.svg floating around.
 * 0. Canvas gives us the WebGL rendering environment,
 * 2. Control the object in the 3D scene
 * 2. I still don't know what the light stuff does,
 * 3. And put the SVG'd-up Scene inside... ?
 * @return {JSX}
 */
const Comp = () => {
    return (
        <div
            className="Logo"
            style={{
                position: "absolute",
                top: 0,
                left: 0,
                height: "100%",
                width: "100%",
            }}
        >
            <Canvas
                gl={{ antialias: true }}
                dpr={window.devicePixelRatio}
                style={{ width: "100%", height: "100%" }}
                shadowMap
                mapSize={[1024, 1024]}
                //camera={{ position: [0, 0, 100] }}
            >
                <ambientLight intensity={1} castShadow mapSize={[1024, 1024]} />
                {/*<spotLight
                    intensity={1}
                    position={[500, 500, 800]}
                    castShadow
                    mapSize={[1024, 1024]}
                />*/}
                <rectAreaLight
                    castShadow
                    color={0xffffff}
                    intensity={1}
                    width={500}
                    height={2000}
                    position={[500, 500, 500]}
                    mapSize={[1024 * 4, 1024 * 4]}
                />

                <Suspense fallback={null}>
                    <Scene />
                </Suspense>
                <Camera />
            </Canvas>
        </div>
    );
};

/*SvgShape.propTypes = {
    color: PropTypes.any,
    index: PropTypes.any,
    shape: PropTypes.shape
}*/

export const meta = {
    title: "3D Logo",
    settings: {},
};

export default Comp;
